import { FC, useMemo } from 'react';
import { defineMessages, FormattedNumber, useIntl } from 'react-intl';
import { PaymentPeriod, PremiumPrice } from 'src/api/internal/schema';
import * as S from './styles';

const msgs = defineMessages({
  bestValue: {
    id: 'new_plans_card_best_value',
    defaultMessage: 'Best value',
  },
  perDay: {
    id: 'new_plans_card_per_day',
    defaultMessage: '{price}/day',
  },
  payOnce: {
    id: 'new_plans_card_pay_once',
    defaultMessage: 'Pay once, yours forever',
  },
  annualSub: {
    id: 'condensed_plans_card_annual_sub',
    defaultMessage: 'Full access, billed yearly',
  },
  monthlySub: {
    id: 'condensed_plans_card_monthly_sub',
    defaultMessage: 'Flexible, cancel anytime',
  },
  monthly: {
    id: 'ea_plans_monthly',
  },
  annual: {
    id: 'ea_plans_annual',
    defaultMessage: 'Annual',
  },
  lifetime: {
    id: 'ea_plans_lifetime',
  },
});

const DAYS_PER_PERIOD: Partial<Record<PaymentPeriod, number>> = {
  month: 30,
  year: 365,
};

function getPricePerDay(price: number, period: PaymentPeriod): number | null {
  const days = DAYS_PER_PERIOD[period];
  if (!days) return null;
  return Math.ceil((price * 100) / days) / 100;
}

function getSavePercent(fullPrice: number, finalPrice: number): number {
  if (fullPrice <= 0) return 0;
  return Math.round(((fullPrice - finalPrice) / fullPrice) * 100);
}

export const PlanCard: FC<{
  price: PremiumPrice;
  selected: boolean;
  bestMonthlyPrice?: number;
  onClick: () => void;
}> = ({ price, selected, bestMonthlyPrice, onClick }) => {
  const intl = useIntl();
  const { paymentPeriod, currency, fullPrice, finalPrice } = price;

  const isAnnual = paymentPeriod === 'year';
  const isLifetime = paymentPeriod === 'lifetime';

  const nameMsg = useMemo(() => {
    if (isAnnual) return msgs.annual;
    if (isLifetime) return msgs.lifetime;
    return msgs.monthly;
  }, [isAnnual, isLifetime]);

  const pricePerDay = useMemo(
    () => getPricePerDay(finalPrice, paymentPeriod),
    [finalPrice, paymentPeriod],
  );

  const savePercent = useMemo(() => {
    if (fullPrice > finalPrice) return getSavePercent(fullPrice, finalPrice);
    if (isAnnual && bestMonthlyPrice) {
      const equivalentYearlyFromMonthly = bestMonthlyPrice * 12;
      if (equivalentYearlyFromMonthly <= 0) return null;
      return getSavePercent(equivalentYearlyFromMonthly, finalPrice);
    }
    return null;
  }, [fullPrice, finalPrice, isAnnual, bestMonthlyPrice]);

  const leftSubText = useMemo(() => {
    if (isLifetime) return intl.formatMessage(msgs.payOnce);
    if (isAnnual) return intl.formatMessage(msgs.annualSub);
    return intl.formatMessage(msgs.monthlySub);
  }, [intl, isLifetime, isAnnual]);

  const comparisonPrice = useMemo(() => {
    if (fullPrice > finalPrice) return fullPrice;
    if (isAnnual && bestMonthlyPrice) return bestMonthlyPrice * 12;
    return null;
  }, [fullPrice, finalPrice, isAnnual, bestMonthlyPrice]);

  return (
    <S.Card
      $selected={selected}
      $isLifetime={isLifetime}
      onClick={onClick}
      data-testid="price-card"
    >
      <div>
        <S.PlanName>
          {intl.formatMessage(nameMsg)}
          {isLifetime && <S.BadgeBest>{intl.formatMessage(msgs.bestValue)}</S.BadgeBest>}
          {savePercent != null && savePercent > 0 && (
            <S.BadgeSave>
              {intl.formatMessage(
                { id: 'new_plans_card_save', defaultMessage: 'Save {percent}%' },
                { percent: savePercent },
              )}
            </S.BadgeSave>
          )}
        </S.PlanName>
        <S.PlanSub>{leftSubText}</S.PlanSub>
      </div>
      <S.PriceCol>
        {comparisonPrice != null && (
          <S.OldPrice>
            <FormattedNumber value={comparisonPrice} style="currency" currency={currency} />
          </S.OldPrice>
        )}
        <S.PlanPrice>
          <FormattedNumber value={finalPrice} style="currency" currency={currency} />
        </S.PlanPrice>
        {!isLifetime && pricePerDay != null && (
          <S.PriceSub>
            {intl.formatMessage(msgs.perDay, {
              price: intl.formatNumber(pricePerDay, { style: 'currency', currency }),
            })}
          </S.PriceSub>
        )}
      </S.PriceCol>
    </S.Card>
  );
};
